import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { QRCodeSVG } from "qrcode.react";
import { Clock, Tag, Gift, Percent, Star, X } from "lucide-react";
import { BRAND } from "@/styles/brand";
import confetti from "canvas-confetti";

const C = BRAND.colors;

const TYPE_CONFIG = {
  percent: { icon: Percent, color: C.green, label: "Rabatt" },
  free: { icon: Gift, color: C.orange, label: "Gratis" },
  experience: { icon: Star, color: "#8B4C70", label: "Erlebnis" },
  welcome: { icon: Gift, color: C.green, label: "Willkommen" },
  birthday: { icon: Gift, color: C.orange, label: "Geburtstag" },
  mystery: { icon: Star, color: C.tan, label: "Mystery" },
};

const STATUS_STYLES = {
  active: { borderColor: C.green, opacity: 1 },
  expiring: { borderColor: C.orange, opacity: 1 },
  expired: { borderColor: C.border, opacity: 0.5 },
  redeemed: { borderColor: C.border, opacity: 0.5 },
};

function getDaysLeft(dateStr) {
  const now = new Date();
  const end = new Date(dateStr);
  const diff = Math.ceil((end - now) / (1000 * 60 * 60 * 24));
  return diff;
}

export default function CouponCard({ coupon, onRedeem }) {
  const [showQR, setShowQR] = useState(false);
  const [redeemed, setRedeemed] = useState(coupon.status === "redeemed");

  const config = TYPE_CONFIG[coupon.type] || TYPE_CONFIG.percent;
  const statusStyle = STATUS_STYLES[coupon.status] || STATUS_STYLES.active;
  const daysLeft = getDaysLeft(coupon.validUntil);
  const isExpiring = daysLeft <= 3 && daysLeft > 0;
  const isActive = coupon.status === "active" || coupon.status === "expiring";
  const IconComp = config.icon;

  const handleRedeem = () => {
    confetti({
      particleCount: 80,
      spread: 60,
      origin: { y: 0.7 },
      colors: [C.green, C.orange, C.tan, "#FFD700"],
    });
    setRedeemed(true);
    setShowQR(true);
    if (onRedeem) onRedeem(coupon.id);
  };

  return (
    <>
      <motion.div
        whileTap={isActive ? { scale: 0.98 } : {}}
        className="relative overflow-hidden"
        style={{
          opacity: statusStyle.opacity,
          borderRadius: 20,
          background: C.bgCard,
          boxShadow: BRAND.shadows.sm,
        }}
      >
        {/* Perforated edge */}
        <div className="absolute left-0 top-0 bottom-0 w-1 flex flex-col justify-around py-4"
          style={{ borderRight: `2px dashed ${C.border}` }}>
          {[...Array(6)].map((_, i) => (
            <div key={i} className="w-3 h-3 rounded-full -ml-1.5"
              style={{ backgroundColor: C.bg }} />
          ))}
        </div>

        <div className="flex items-stretch">
          {/* Left: Discount Value */}
          <div className="w-24 flex flex-col items-center justify-center py-5 pl-4"
            style={{ backgroundColor: config.color + "10" }}>
            <IconComp size={24} style={{ color: config.color }} />
            <span className="text-2xl font-extrabold mt-1"
              style={{ color: config.color, fontFamily: BRAND.typography.heading }}>
              {coupon.discount}
            </span>
            <span className="text-[10px] font-semibold uppercase tracking-wider mt-0.5"
              style={{ color: config.color }}>
              {config.label}
            </span>
          </div>

          {/* Right: Details */}
          <div className="flex-1 p-4 pl-5">
            <h3 className="text-[15px] font-bold leading-snug"
              style={{ color: C.text, fontFamily: BRAND.typography.heading }}>
              {coupon.title}
            </h3>
            <p className="text-[13px] leading-snug mt-1"
              style={{ color: C.textMuted }}>
              {coupon.description}
            </p>

            <div className="flex items-center justify-between mt-3">
              {/* Expiry */}
              <div className="flex items-center gap-1.5">
                <Clock size={14} style={{ color: isExpiring ? C.orange : C.textMuted }} />
                <span className="text-[12px] font-medium"
                  style={{ color: isExpiring ? C.orange : C.textMuted }}>
                  {coupon.status === "redeemed" ? "Eingelöst" :
                    coupon.status === "expired" ? "Abgelaufen" :
                      daysLeft <= 0 ? "Heute!" :
                        daysLeft === 1 ? "Noch 1 Tag" :
                          `Noch ${daysLeft} Tage`}
                </span>
              </div>

              {/* Redeem Button */}
              {isActive && !redeemed && (
                <motion.button
                  whileTap={{ scale: 0.95 }}
                  whileHover={{ scale: 1.05 }}
                  onClick={handleRedeem}
                  className="px-4 py-2 rounded-xl text-[13px] font-bold text-white"
                  style={{ backgroundColor: config.color }}>
                  Einlösen
                </motion.button>
              )}

              {redeemed && (
                <span className="text-[12px] font-semibold px-3 py-1 rounded-full"
                  style={{ backgroundColor: C.green + "15", color: C.green }}>
                  Eingelöst
                </span>
              )}
            </div>

            {coupon.conditions && (
              <p className="text-[11px] mt-2" style={{ color: C.textMuted }}>
                {coupon.conditions}
              </p>
            )}
          </div>
        </div>
      </motion.div>

      {/* QR Code Modal */}
      <AnimatePresence>
        {showQR && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center"
            style={{ backgroundColor: "rgba(0,0,0,0.5)", backdropFilter: "blur(6px)" }}
            onClick={() => setShowQR(false)}>
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.8, opacity: 0 }}
              className="p-8 rounded-[28px] text-center max-w-[320px] w-full mx-4"
              style={{ backgroundColor: C.bgCard }}
              onClick={e => e.stopPropagation()}>
              <button onClick={() => setShowQR(false)}
                className="absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center"
                style={{ backgroundColor: C.bg }}>
                <X size={16} style={{ color: C.textMuted }} />
              </button>
              <p className="text-lg font-bold mb-4" style={{ color: C.text, fontFamily: BRAND.typography.heading }}>
                Zeig das an der Kasse
              </p>
              <div className="p-4 rounded-2xl inline-block" style={{ backgroundColor: C.bg }}>
                <QRCodeSVG value={`frankysfarm:coupon:${coupon.id}`} size={180}
                  fgColor={C.green} bgColor="transparent" />
              </div>
              <p className="text-sm mt-4 font-medium" style={{ color: C.green }}>
                {coupon.title}
              </p>
              <p className="text-xs mt-1" style={{ color: C.textMuted }}>
                Gültig bis {new Date(coupon.validUntil).toLocaleDateString("de-DE")}
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
